export const MAX_MODEL_REQUESTS = 2_147_483_647;
export const DEFAULT_STRUCTURED_OUTPUT_TOKENS = 65_536;
export const MAX_STRUCTURED_OUTPUT_TOKENS = 2_147_483_647;
/**
 * The research (planning) clock was ten minutes with a fifteen-minute ceiling
 * until 2026-09-10, then twenty with a thirty-minute ceiling. Since 2026-10-09
 * every time limit exists only to catch a catastrophic stop, never to be why
 * good work fails, so it is 45 minutes by default and an hour at most. The
 * scoping session's own cap mirrors these (`readOnlySession.ts`), and the
 * stale-run sweepers measure from the last liveness stamp, not from start.
 *
 * The development clock covers the WHOLE sandbox /go pipeline: implement plus
 * its review/fix rounds. At thirty minutes (55 at most) it killed good builds
 * mid-round; two hours (four at most) from 2026-09-30; six hours by default
 * and eight at most since 2026-10-09. Every sandbox-side bound (harness cap,
 * sandbox TTL, model capability TTL, reconciliation horizon, Temporal
 * activity timeouts) derives from or is pinned against
 * `MAX_TEAM_RUN_ELAPSED_MS.development`.
 */
export const DEFAULT_TEAM_RUN_LIMITS = {
    structuredOutput: { maxTokens: DEFAULT_STRUCTURED_OUTPUT_TOKENS },
    planning: { maxRequests: 1024, maxCostMillicents: 1_000_000, maxElapsedMs: 45 * 60_000 },
    development: { maxRequests: 4096, maxCostMillicents: 2_500_000, maxElapsedMs: 6 * 60 * 60_000 },
};
export const MAX_TEAM_RUN_ELAPSED_MS = {
    planning: 60 * 60_000,
    development: 8 * 60 * 60_000,
};
/**
 * Automatic turns a Team starts on its own each day (`desks.daily_turn_budget`):
 * the default when the column is NULL, and the most the charter accepts (the
 * column is a Postgres integer). Shared so the Team settings sheet shows the
 * same default and bound the backend enforces.
 */
export const DEFAULT_DAILY_TURN_BUDGET = 48;
export const MAX_DAILY_TURN_BUDGET = 2_147_483_647;
const stages = ["planning", "development"];
const fields = ["maxRequests", "maxCostMillicents", "maxElapsedMs"];
function isRecord(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
}
export function parseTeamRunLimits(value, previous = DEFAULT_TEAM_RUN_LIMITS) {
    if (value === null)
        return { ok: true, value: resolveTeamRunLimits({}) };
    if (!isRecord(value) ||
        Object.keys(value).some((key) => key !== "structuredOutput" && !stages.includes(key))) {
        return {
            ok: false,
            error: "Run limits must contain planning, development, or structuredOutput settings.",
        };
    }
    const result = {
        planning: { ...previous.planning },
        development: { ...previous.development },
        structuredOutput: {
            ...(previous.structuredOutput ?? DEFAULT_TEAM_RUN_LIMITS.structuredOutput),
        },
    };
    if ("structuredOutput" in value) {
        const output = value.structuredOutput;
        if (!isRecord(output) || Object.keys(output).some((key) => key !== "maxTokens")) {
            return { ok: false, error: "Invalid planning and setup response settings." };
        }
        if ("maxTokens" in output) {
            const limit = output.maxTokens;
            if (typeof limit !== "number" ||
                !Number.isSafeInteger(limit) ||
                limit < 1 ||
                limit > MAX_STRUCTURED_OUTPUT_TOKENS) {
                return {
                    ok: false,
                    error: `Response tokens must be a whole number from 1 to ${MAX_STRUCTURED_OUTPUT_TOKENS.toLocaleString()}.`,
                };
            }
            result.structuredOutput.maxTokens = limit;
        }
    }
    for (const stage of stages) {
        if (!(stage in value))
            continue;
        const settings = value[stage];
        if (!isRecord(settings) ||
            Object.keys(settings).some((key) => !fields.includes(key))) {
            return { ok: false, error: `Invalid ${stage} run settings.` };
        }
        for (const field of fields) {
            if (!(field in settings))
                continue;
            const number = settings[field];
            const maximum = field === "maxRequests"
                ? MAX_MODEL_REQUESTS
                : field === "maxElapsedMs"
                    ? MAX_TEAM_RUN_ELAPSED_MS[stage]
                    : Number.MAX_SAFE_INTEGER;
            const minimum = field === "maxElapsedMs" ? 60_000 : 1;
            if (typeof number !== "number" ||
                !Number.isSafeInteger(number) ||
                number < minimum ||
                number > maximum) {
                const label = stage === "planning" ? "Research" : "Development";
                return {
                    ok: false,
                    error: field === "maxElapsedMs"
                        ? `${label} duration must be between 1 and ${maximum / 60_000} minutes.`
                        : field === "maxCostMillicents"
                            ? `${label} budget must be a positive dollar amount within the supported range.`
                            : `${label} model calls must be a whole number from 1 to ${maximum.toLocaleString()}.`,
                };
            }
            result[stage][field] = number;
        }
    }
    return { ok: true, value: result };
}
/**
 * What a Team stores: only the values that differ from today's defaults.
 * Storing the whole resolved set pinned a Team to the defaults of the day it
 * was last saved, so a Team configured before 2026-09-30 never got the
 * longer build window, and a `null` reset stored a fresh copy of the
 * defaults instead of clearing them.
 */
export function teamRunLimitsOverride(limits) {
    const override = {};
    for (const stage of stages) {
        const changed = {};
        for (const field of fields) {
            if (limits[stage][field] !== DEFAULT_TEAM_RUN_LIMITS[stage][field])
                changed[field] = limits[stage][field];
        }
        if (Object.keys(changed).length > 0)
            override[stage] = changed;
    }
    if (limits.structuredOutput.maxTokens !== DEFAULT_TEAM_RUN_LIMITS.structuredOutput.maxTokens) {
        override.structuredOutput = { maxTokens: limits.structuredOutput.maxTokens };
    }
    return override;
}
export function resolveTeamRunLimits(value) {
    const parsed = parseTeamRunLimits(value ?? {});
    return parsed.ok
        ? parsed.value
        : {
            structuredOutput: { ...DEFAULT_TEAM_RUN_LIMITS.structuredOutput },
            planning: { ...DEFAULT_TEAM_RUN_LIMITS.planning },
            development: { ...DEFAULT_TEAM_RUN_LIMITS.development },
        };
}
export const MILLICENTS_PER_DOLLAR = 100_000;
export const DEFAULT_TICKET_BUDGET = {
    maxCostMillicents: 75 * MILLICENTS_PER_DOLLAR,
    maxElapsedMs: 24 * 60 * 60_000,
};
/** The longest clock a Team may give one ticket: a week of run time. */
export const MAX_TICKET_BUDGET_ELAPSED_MS = 7 * 24 * 60 * 60_000;
/** The shortest: one hour, so one build attempt can always start. */
export const MIN_TICKET_BUDGET_ELAPSED_MS = 60 * 60_000;
/** The most one ticket may cost: $10,000. */
export const MAX_TICKET_BUDGET_MILLICENTS = 10_000 * MILLICENTS_PER_DOLLAR;
/**
 * The budget a Team's old `run_limits` amount to, or null when the Team never
 * set a money or time limit there: the planning and development dollar caps
 * added together, and the longer of the two clocks. So no Team that raised
 * its old caps ends up with less room on a ticket than one plan and one build
 * had before.
 */
export function ticketBudgetFromRunLimits(runLimits) {
    if (!isRecord(runLimits))
        return null;
    const touched = stages.some((stage) => {
        const settings = runLimits[stage];
        return isRecord(settings) && ("maxCostMillicents" in settings || "maxElapsedMs" in settings);
    });
    if (!touched)
        return null;
    const resolved = resolveTeamRunLimits(runLimits);
    return {
        maxCostMillicents: resolved.planning.maxCostMillicents + resolved.development.maxCostMillicents,
        maxElapsedMs: Math.max(resolved.planning.maxElapsedMs, resolved.development.maxElapsedMs),
    };
}
function storedPositive(value) {
    const number = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
    return typeof number === "number" && Number.isSafeInteger(number) && number > 0 ? number : null;
}
/**
 * The Team's ticket budget on read. A value the Team saved for the new budget
 * (`desks.ticket_budget_millicents`, `desks.ticket_budget_elapsed_ms`) wins
 * as saved. Otherwise each field is the larger of today's default and what
 * the Team's old `run_limits` map to, so no Team gets a smaller budget than
 * it had.
 */
export function resolveTicketBudget(input) {
    const mapped = ticketBudgetFromRunLimits(input.runLimits);
    return {
        maxCostMillicents: storedPositive(input.maxCostMillicents) ??
            Math.max(DEFAULT_TICKET_BUDGET.maxCostMillicents, mapped?.maxCostMillicents ?? 0),
        maxElapsedMs: storedPositive(input.maxElapsedMs) ??
            Math.max(DEFAULT_TICKET_BUDGET.maxElapsedMs, mapped?.maxElapsedMs ?? 0),
    };
}
/**
 * The charter's `ticketBudget` field: either field may be given, and `null`
 * for a field (or for the whole value) puts it back to the default.
 */
export function parseTicketBudget(value) {
    if (value === null)
        return { ok: true, value: { maxCostMillicents: null, maxElapsedMs: null } };
    if (!isRecord(value) ||
        Object.keys(value).some((key) => key !== "maxCostMillicents" && key !== "maxElapsedMs")) {
        return { ok: false, error: "The ticket budget takes maxCostMillicents and maxElapsedMs." };
    }
    const out = {};
    if ("maxCostMillicents" in value) {
        const cost = value.maxCostMillicents;
        if (cost === null)
            out.maxCostMillicents = null;
        else if (typeof cost !== "number" ||
            !Number.isSafeInteger(cost) ||
            cost < 1 ||
            cost > MAX_TICKET_BUDGET_MILLICENTS) {
            return {
                ok: false,
                error: `The ticket budget must be a dollar amount above zero and at most $${(MAX_TICKET_BUDGET_MILLICENTS / MILLICENTS_PER_DOLLAR).toLocaleString("en-US")}.`,
            };
        }
        else
            out.maxCostMillicents = cost;
    }
    if ("maxElapsedMs" in value) {
        const elapsed = value.maxElapsedMs;
        if (elapsed === null)
            out.maxElapsedMs = null;
        else if (typeof elapsed !== "number" ||
            !Number.isSafeInteger(elapsed) ||
            elapsed < MIN_TICKET_BUDGET_ELAPSED_MS ||
            elapsed > MAX_TICKET_BUDGET_ELAPSED_MS) {
            return {
                ok: false,
                error: `The ticket time must be between ${MIN_TICKET_BUDGET_ELAPSED_MS / 3_600_000} and ${MAX_TICKET_BUDGET_ELAPSED_MS / 3_600_000} hours.`,
            };
        }
        else
            out.maxElapsedMs = elapsed;
    }
    return { ok: true, value: out };
}
/** What is left of the budget, never below zero, and whether either side ran out. */
export function ticketBudgetLeft(budget, spend) {
    const millicents = Math.max(0, budget.maxCostMillicents - Math.max(0, spend.spentMillicents));
    const elapsedMs = Math.max(0, budget.maxElapsedMs - Math.max(0, spend.elapsedMs));
    return { millicents, elapsedMs, spent: millicents <= 0 || elapsedMs <= 0 };
}
function dollarsWords(millicents) {
    const dollars = Math.max(0, millicents) / MILLICENTS_PER_DOLLAR;
    return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}
function hoursWords(ms) {
    const minutes = Math.max(0, Math.round(ms / 60_000));
    if (minutes < 60)
        return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
/** "$75.12 of $75 · 3 h 10 min of 24 h": what the ticket spent against its budget. */
export function ticketBudgetWords(budget, spend) {
    return `${dollarsWords(spend.spentMillicents)} of ${dollarsWords(budget.maxCostMillicents)} · ${hoursWords(spend.elapsedMs)} of ${hoursWords(budget.maxElapsedMs)}`;
}
const DOLLARS_WORDS = String.raw `\$\d+(?:\.\d{2})?`;
const HOURS_WORDS = String.raw `(?:\d+ min|\d+ h(?: \d+ min)?)`;
/**
 * What `ticketBudgetWords` writes, found in a sentence that carries it, so an
 * ask written when a ticket stopped can say it again with today's figures.
 */
export const TICKET_BUDGET_WORDS_PATTERN = new RegExp(`${DOLLARS_WORDS} of ${DOLLARS_WORDS} · ${HOURS_WORDS} of ${HOURS_WORDS}`, "g");
//# sourceMappingURL=teamRunLimits.js.map